import {
  and,
  ControlProps,
  isDescriptionHidden,
  RankedTester,
  rankWith,
  schemaMatches,
  schemaTypeIs,
  Tester,
  uiTypeIs,
} from "@jsonforms/core";
import { useFocus } from "@jsonforms/material-renderers";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { FormHelperText } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import merge from "lodash/merge";
import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";

import isEmpty from "lodash/isEmpty";
import { Constants } from "hhn-commons";

export const DatePickerRenderer = (props: ControlProps) => {
  const [focused, onFocus, onBlur] = useFocus();
  const {
    description,
    id,
    errors,
    label,
    uischema,
    visible,
    enabled,
    required,
    path,
    handleChange,
    data,
    config,
  } = props;
  const isValid = errors.length === 0;
  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const showDescription = !isDescriptionHidden(
    visible,
    description,
    focused,
    appliedUiSchemaOptions.showUnfocusedDescription,
  );

  const [open, setOpen] = useState<boolean>(false);
  const appliedDateFormat = Constants.HHN_DATE_FORMAT;

  const views = appliedUiSchemaOptions.views ?? ["year", "day"];

  const firstFormHelperText = showDescription
    ? description
    : !isValid
      ? errors
      : null;
  const secondFormHelperText = showDescription && !isValid ? errors : null;

  const [localValue, setLocalValue] = useState<Dayjs | null>(
    data ? dayjs(data) : null,
  );

  const onChange = (newValue: Dayjs | null) => {
    setLocalValue(newValue);
    if (newValue && newValue.isValid()) {
      handleChange(path, newValue.valueOf());
    } else {
      handleChange(path, dayjs(null));
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        label={label}
        value={localValue}
        onChange={onChange}
        format={appliedDateFormat}
        views={views}
        disabled={!enabled}
        disableFuture={appliedUiSchemaOptions?.disableFuture}
        disablePast={appliedUiSchemaOptions?.disablePast}
        slotProps={{
          actionBar: () => ({
            actions: ["clear", "cancel", "accept"],
          }),
          textField: {
            id: id + "-input",
            required: required && !appliedUiSchemaOptions.hideRequiredAsterisk,
            autoFocus: appliedUiSchemaOptions.focus,
            error: !isValid,
            fullWidth: !appliedUiSchemaOptions.trim,
            onFocus,
            onBlur,
            slotProps: {
              htmlInput: {
                type: "text",
              },
              inputLabel: data
                ? {
                    shrink: true,
                  }
                : undefined,
            },
          },
        }}
      />
      <FormHelperText error={!isValid && !showDescription}>
        {firstFormHelperText}
      </FormHelperText>
      <FormHelperText error={!isValid}>{secondFormHelperText}</FormHelperText>
    </LocalizationProvider>
  );
};

const formatIs = (expectedFormat: string): Tester =>
  schemaMatches(schema => !isEmpty(schema) && schema.format === expectedFormat);

const isDateControl = and(
  uiTypeIs("Control"),
  schemaTypeIs("number"),
  formatIs("date"),
);

export const datePickerTester: RankedTester = rankWith(7, isDateControl);

export default withJsonFormsControlProps(DatePickerRenderer);
